<template>
  <Dialog :common-dialog="quotationDialog" :dialog-width="dialog_width">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between width-100 pr-2">
        <div class="d-flex align-center">
          <v-avatar color="cyan lighten-5" size="44" rounded class="mr-3">
            <v-icon color="cyan" large>mdi-format-quote-close</v-icon>
          </v-avatar>
          <div>
            <h3 class="font-weight-700 font-size-19 m-0 color-custom-blue">
              Select an accepted quotation
            </h3>
            <p
              class="font-size-12 font-wight-500 grey--text text--darken-1 m-0"
            >
              Only customer-accepted quotations can be linked to a PO
            </p>
          </div>
        </div>
        <v-btn icon @click="close">
          <v-icon color="grey darken-1">mdi-close</v-icon>
        </v-btn>
      </div>
    </template>

    <template v-slot:body>
      <v-sheet v-if="loading" class="mb-4 pa-4 rounded-lg border d-flex align-center justify-center">
        <v-progress-circular
          indeterminate
          color="cyan"
          size="24"
          class="mr-3"
        ></v-progress-circular>
        <span class="font-weight-700 grey--text text--darken-2">
          Loading quotations...
        </span>
      </v-sheet>

      <!-- Customer Section & Search Filters -->
      <v-card flat class="grey lighten-5 rounded-lg px-3 pt-3 pb-2 mb-4 border" v-if="!loading">
        <div class="d-flex align-center justify-space-between mb-0">
          <div
            class="d-flex align-center font-weight-700 font-size-12 grey--text text--darken-2 text-uppercase"
            style="letter-spacing: 0.5px"
          >
            <v-icon small color="cyan" class="mr-1"
              >mdi-account-filter-outline</v-icon
            >
            Customer Filter & Search
          </div>
          <v-chip
            v-if="selectedCustomer"
            small
            close
            color="cyan lighten-4"
            text-color="cyan darken-2"
            class="font-weight-700"
            @click:close="selectedCustomer = null"
          >
            Customer: {{ selectedCustomer }}
          </v-chip>
        </div>

        <v-row dense>
          <!-- Customer Selection / Search Field -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="selectedCustomer"
              :items="customerOptions"
              placeholder="Search or select customer..."
              prepend-inner-icon="mdi-domain"
              dense
              outlined
              solo
              flat
              hide-details
              clearable
              class="rounded-lg bg-white"
              color="cyan"
            ></v-autocomplete>
          </v-col>

          <!-- Quotation & Job Search Field -->
          <v-col cols="12" md="6">
            <v-text-field
              ref="quotationSearch"
              v-model="searchQuery"
              @keydown.enter.prevent="$emit('search', searchQuery || null)"
              placeholder="Quotation no., subject, or customer name..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              solo
              flat
              hide-details
              clearable
              class="rounded-lg bg-white"
              color="cyan"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Customer Quick Select Chips -->
        <div
          v-if="customerOptions.length > 0"
          class="d-flex align-center flex-wrap gap-1 mt-2"
        >
          <span
            class="font-size-11 grey--text text--darken-1 font-weight-medium mr-2"
            >Quick Filter:</span
          >
          <v-chip
            small
            :color="!selectedCustomer ? 'cyan' : 'grey lighten-3'"
            :text-color="!selectedCustomer ? 'white' : 'grey darken-2'"
            class="font-weight-700 mr-1 mb-1 cursor-pointer"
            @click="selectedCustomer = null"
          >
            All Customers ({{ itemsToDisplay.length }})
          </v-chip>
          <v-chip
            v-for="(cust, idx) in customerOptions"
            :key="idx"
            small
            :color="selectedCustomer === cust ? 'cyan' : 'grey lighten-3'"
            :text-color="selectedCustomer === cust ? 'white' : 'grey darken-2'"
            class="font-weight-700 mr-1 mb-1 cursor-pointer"
            @click="selectedCustomer = selectedCustomer === cust ? null : cust"
          >
            {{ cust }} ({{ getQuotationCountByCustomer(cust) }})
          </v-chip>
        </div>
      </v-card>

      <!-- Table Container -->
      <v-card v-if="!loading" flat class="border rounded-lg overflow-hidden mb-4">
        <v-simple-table dense class="custom-quotation-table">
          <template v-slot:default>
            <thead>
              <tr class="grey lighten-4">
                <th
                  class="text-left font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Quotation
                </th>
                <th
                  class="text-left font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Quotation Subject
                </th>
                <th
                  class="text-left font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Customer
                </th>
                <!-- <th
                  class="text-left font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Job
                </th> -->
                <th
                  class="text-left font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Value
                </th>
                <th
                  class="text-center font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Status
                </th>
                <th
                  class="text-center font-weight-700 font-size-11 text-uppercase grey--text text--darken-1"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in filteredQuotations"
                :key="index"
                class="cursor-pointer"
                @click="selectQuotation(item)"
              >
                <td class="py-3">
                  <div
                    class="font-weight-700 grey--text text--darken-4 font-size-14"
                  >
                    {{ item.barcode }}
                  </div>
                  <div class="font-size-12 grey--text text--darken-1">
                    {{ getLineItemCount(item) }}
                    line items
                  </div>
                </td>
                <td
                  class="py-3 font-size-13 grey--text text--darken-3"
                  style="max-width: 200px"
                >
                  {{
                    item.job_title ||
                    item.subject ||
                    "-"
                  }}
                </td>
                <td
                  class="py-3 font-weight-medium font-size-13 grey--text text--darken-3"
                >
                  {{
                    item.customer_name ||
                    item.customer?.display_name ||
                    item.customer?.name ||
                    "-"
                  }}
                </td>
                <!-- <td class="py-3 font-size-13 grey--text text--darken-2">
                  {{ item.job_barcode || "-" }}
                </td> -->
                <td
                  class="py-3 text-left font-weight-700 font-size-13 grey--text text--darken-3"
                >
                  {{ item.total == null ? "-" : "$" + formatAmount(item.total) }}
                </td>
                <td class="py-3 text-center">
                  <v-chip
                    small
                    :color="getStatusColor(item)"
                    :text-color="getStatusTextColor(item)"
                    class="font-weight-700 px-2"
                  >
                    ● {{ getStatusText(item) }}
                  </v-chip>
                  <v-chip
                    v-if="false"
                    small
                    color="green lighten-5"
                    text-color="green darken-2"
                    class="font-weight-700 px-2"
                  >
                    ● Accepted
                  </v-chip>
                  <div class="font-size-11 grey--text text--darken-1 mt-1">
                    {{ item.accepted_date || item.updated_at || "-" }}
                  </div>
                </td>
                <td class="py-3 text-center">
                  <v-btn
                    small
                    depressed
                    color="cyan"
                    class="white--text text-capitalize custom-bold-button rounded-lg px-3"
                    @click.stop="selectQuotation(item)"
                  >
                    <v-icon small left>mdi-check</v-icon>
                    Select
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div v-if="hasMore" class="text-center pa-3 border-top">
          <v-btn outlined color="cyan darken-1" :loading="loadingMore" :disabled="loadingMore" @click="$emit('load-more', searchQuery)">
            Load more
          </v-btn>
        </div>
      </v-card>

      <!-- Info Banner -->
      <v-sheet class="cyan lighten-5 rounded-lg pa-3 mb-0 d-flex align-center">
        <v-icon color="cyan darken-2" class="mr-2"
          >mdi-information-outline</v-icon
        >
        <span class="font-size-13 cyan--text text--darken-3 font-weight-medium">
          Linking a quotation keeps cost-to-quote variance visible on the job
          and copies any unlinked items across.
        </span>
      </v-sheet>
    </template>

    <template v-slot:action>
      <div class="d-flex align-center justify-space-between width-100 py-2">
        <div class="font-size-13 grey--text text--darken-1 font-weight-medium">
          {{ acceptedQuotationCount }} accepted ·
          {{ pendingCustomerApprovalCount }} pending customer approval
        </div>
        <v-btn
          outlined
          color="grey darken-1"
          class="text-capitalize custom-grey-border custom-bold-button rounded-lg px-5"
          @click="close"
        >
          Cancel
        </v-btn>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";

export default {
  name: "SelectAcceptedQuotationModal",
  components: {
    Dialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    quotationList: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedCustomer: null,
    };
  },
  computed: {
    dialog_width() {
      return 1200;
    },
    quotationDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    customerOptions() {
      const set = new Set();
      this.itemsToDisplay.forEach((item) => {
        const customerName =
          item.customer_name || item.customer?.display_name || item.customer?.name;
        if (customerName) {
          set.add(customerName);
        }
      });
      return Array.from(set);
    },
    itemsToDisplay() {
      return this.quotationList || [];
    },
    acceptedQuotationCount() {
      return this.itemsToDisplay.filter((item) => Number(item.status) === 2).length;
    },
    pendingCustomerApprovalCount() {
      return this.itemsToDisplay.filter((item) => Number(item.status) === 5).length;
    },
    filteredQuotations() {
      let result = this.itemsToDisplay;

      if (this.selectedCustomer) {
        const cust = this.selectedCustomer.toLowerCase();
        result = result.filter(
          (item) => {
            const customerName =
              item.customer_name ||
              item.customer?.display_name ||
              item.customer?.name ||
              "";
            return customerName.toLowerCase().includes(cust);
          }
        );
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter(
          (item) =>
            (item.barcode && item.barcode.toLowerCase().includes(q)) ||
            ((item.customer_name ||
              item.customer?.display_name ||
              item.customer?.name) &&
              (item.customer_name ||
                item.customer?.display_name ||
                item.customer?.name
              ).toLowerCase().includes(q)) ||
            (item.job_title && item.job_title.toLowerCase().includes(q)) ||
            (item.job_barcode && item.job_barcode.toLowerCase().includes(q))
        );
      }

      return result;
    },
  },
  methods: {
    getQuotationCountByCustomer(customerName) {
      if (!customerName) return 0;
      return this.itemsToDisplay.filter(
        (item) => {
          const itemCustomerName =
            item.customer_name ||
            item.customer?.display_name ||
            item.customer?.name;
          return itemCustomerName === customerName;
        }
      ).length;
    },
    formatAmount(val) {
      if (!val) return "0.00";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getLineItemCount(item) {
      const count = [
        item.line_items_count,
        item.line_item_count,
        item.total_items,
      ].find((value) => value !== null && value !== undefined && value !== "");

      return count === undefined ? 0 : Number(count) || 0;
    },
    getStatusText(item) {
      const statuses = {
        1: "Draft",
        2: "Accepted",
        3: "Rejected",
        5: "Sent",
        6: "Cancelled",
        11: "Submit For Approval",
      };
      return statuses[Number(item.status)] || "Unknown";
    },
    getStatusColor(item) {
      const colors = {
        1: "cyan lighten-5",
        2: "green lighten-5",
        3: "red lighten-5",
        5: "orange lighten-5",
        6: "red lighten-5",
        11: "#fff0e0",
      };
      return colors[Number(item.status)] || "grey lighten-5";
    },
    getStatusTextColor(item) {
      const colors = {
        1: "cyan darken-2",
        2: "green darken-2",
        3: "red darken-2",
        5: "orange darken-2",
        6: "red darken-2",
        11: "#ff7300",
      };
      return colors[Number(item.status)] || "grey darken-2";
    },
    close() {
      this.$emit("input", false);
    },
    selectQuotation(item) {
      this.$emit("select", item);
      this.close();
    },
  },
};
</script>

<style scoped>
.custom-quotation-table th {
  border-bottom: 1px solid #e2e8f0 !important;
}
.custom-quotation-table td {
  border-bottom: 1px solid #f1f5f9 !important;
}
.text-slate-900 {
  color: #0f172a;
}
.text-slate-800 {
  color: #1e293b;
}
.text-slate-700 {
  color: #334155;
}
.text-slate-600 {
  color: #475569;
}
.text-slate-500 {
  color: #64748b;
}
.text-slate-400 {
  color: #94a3b8;
}
.bg-slate-50 {
  background-color: #f8fafc;
}
.bg-white {
  background-color: #ffffff !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
